import { useCallback, useEffect, useState } from 'react';

const CACHE_KEY = 'saloni_exhibitions_cache_v1';
const CACHE_TTL_MS = 5 * 60 * 1000;
let inFlightExhibitions = null;

function readExhibitionsCache() {
  try {
    const raw = sessionStorage.getItem(CACHE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed?.ts || Date.now() - parsed.ts > CACHE_TTL_MS) return null;
    if (parsed.payload?.status !== 'ready') return null;
    return parsed.payload;
  } catch {
    return null;
  }
}

function writeExhibitionsCache(payload) {
  try {
    sessionStorage.setItem(CACHE_KEY, JSON.stringify({ ts: Date.now(), payload }));
  } catch {
    /* quota / private mode */
  }
}

async function fetchExhibitions() {
  if (inFlightExhibitions) return inFlightExhibitions;

  const promise = (async () => {
    const r = await fetch('/api/shows.php');
    const data = await r.json().catch(() => ({}));
    if (!data.ok) {
      return { status: 'error', upcoming: [], recent: [] };
    }
    return {
      status: 'ready',
      upcoming: Array.isArray(data.upcoming) ? data.upcoming : [],
      recent: Array.isArray(data.recent) ? data.recent : [],
    };
  })();

  inFlightExhibitions = promise;
  promise.finally(() => {
    if (inFlightExhibitions === promise) {
      inFlightExhibitions = null;
    }
  });
  return promise;
}

/**
 * Single fetch for home exhibition banner + section (upcoming is soonest first per API).
 */
export function useExhibitions() {
  const cached = readExhibitionsCache();
  const [state, setState] = useState(
    cached || {
      status: 'loading',
      upcoming: [],
      recent: [],
    },
  );

  const refetch = useCallback(async () => {
    try {
      const next = await fetchExhibitions();
      setState(next);
      if (next.status === 'ready') writeExhibitionsCache(next);
      return next;
    } catch {
      const err = { status: 'error', upcoming: [], recent: [] };
      setState(err);
      return err;
    }
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const next = await fetchExhibitions();
        if (cancelled) return;
        setState(next);
        if (next.status === 'ready') writeExhibitionsCache(next);
      } catch {
        if (!cancelled) setState({ status: 'error', upcoming: [], recent: [] });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  return { ...state, refetch };
}
